/**
 * features/hr/StaffDirectory.tsx
 * ------------------------------------------------------------------
 * HR DOMAIN — Staff Directory.
 *
 * Operational in the demo:
 *   - clicking any staff card opens StaffProfile via onOpenStaff
 *   - "Add staff" modal writes a new hrStaff doc to Firestore
 *   - filter chips + search
 *
 * Teaching staff with linkedTeacherId show a "Linked to academic
 * record" marker — that lookup against the academic DataContext is
 * the only point HR touches the academic data model (read-only).
 * ------------------------------------------------------------------ */
import { useMemo, useState, useEffect } from 'react';
import {
  Users,
  Search,
  Link2,
  GraduationCap,
  Plus,
  Upload,
  UserPlus,
  Save,
  Loader2,
} from 'lucide-react';
import { GradientHero, SoftCard, StatChip, AccentButton, glassFieldClass } from '@/features/finance/ui';
import { FormModal } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { useData } from '@/contexts/DataContext';
import { useHrData } from '@/features/hr/useHrData';
import StaffMigrationImport from '@/features/hr/StaffMigrationImport';
import DeductionImport from '@/features/hr/DeductionImport';
import { hr, newId } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { useAuth } from '@/contexts/AuthContext';
import { HR_COLLECTIONS, nextStaffNumber } from '@/lib/hr';
import {
  staffFullName,
  staffInitials,
  STAFF_CATEGORY_LABELS,
  RESIDENCY_LABELS,
  type StaffCategory,
  type ResidencyStatus,
} from '@/lib/hr';

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  on_leave: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  suspended: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  exited: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

type CategoryFilter = StaffCategory | 'all';

interface Props {
  onOpenStaff: (staffId: string) => void;
}

export default function StaffDirectory({ onOpenStaff }: Props) {
  const data = useHrData();
  const { teachers } = useData();
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState<CategoryFilter>('all');
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingDed, setImportingDed] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.staff
      .filter((s) => cat === 'all' || s.category === cat)
      .filter(
        (s) =>
          !q ||
          staffFullName(s).toLowerCase().includes(q) ||
          (s.jobTitle ?? '').toLowerCase().includes(q) ||
          (s.department ?? '').toLowerCase().includes(q),
      )
      .sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? ''));
  }, [data.staff, search, cat]);

  const teacherLinked = (id?: string) =>
    id ? teachers.find((t) => t.id === id) : undefined;

  const cats: CategoryFilter[] = [
    'all',
    'teaching',
    'management',
    'admin',
    'support',
    'ancillary',
  ];

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Users}
        title="Staff directory"
        subtitle="Every employee — teaching and non-teaching"
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setImporting(true)}
              className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl bg-white/15 text-white hover:bg-white/25 backdrop-blur"
            >
              <Upload size={15} /> Import balances
            </button>
            <button
              onClick={() => setImportingDed(true)}
              className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-xl bg-white/15 text-white hover:bg-white/25 backdrop-blur"
            >
              <Upload size={15} /> Import deductions
            </button>
            <AccentButton icon={Plus} onClick={() => setAdding(true)}>
              Add staff
            </AccentButton>
          </div>
        }
      />

      {data.loading ? (
        <div className="flex justify-center py-16">
          <Spinner label="Loading staff…" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip tone="blue" value={data.staff.length} label="All staff" />
            <StatChip
              tone="purple"
              value={data.staff.filter((s) => s.category === 'teaching').length}
              label="Teaching"
            />
            <StatChip
              tone="slate"
              value={data.staff.filter((s) => s.category !== 'teaching').length}
              label="Non-teaching"
            />
            <StatChip
              tone="emerald"
              value={data.staff.filter((s) => s.status === 'active').length}
              label="Active"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, role, department…"
                className={glassFieldClass('pl-9 py-2')}
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {cats.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    cat === c
                      ? 'bg-[var(--accent-strong,#1b3a6b)] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {c === 'all' ? 'All' : STAFF_CATEGORY_LABELS[c]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filtered.map((s) => {
              const t = teacherLinked(s.linkedTeacherId);
              return (
                <button
                  key={s.id}
                  onClick={() => onOpenStaff(s.id)}
                  className="text-left"
                >
                  <SoftCard pad="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[var(--accent-from,#2d5295)] to-[var(--accent-to,#1b3a6b)] text-white flex items-center justify-center text-sm font-bold shrink-0">
                        {staffInitials(s)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-800 dark:text-gray-100 truncate">
                          {staffFullName(s)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {s.jobTitle}
                          {s.department ? ` · ${s.department}` : ''}
                        </p>
                      </div>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-lg shrink-0 ${
                          STATUS_BADGE[s.status] ?? STATUS_BADGE.active
                        }`}
                      >
                        {s.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                      <span>#{s.employeeNo}</span>
                      <span>·</span>
                      <span>{RESIDENCY_LABELS[s.residency]}</span>
                      {t ? (
                        <span className="ml-auto flex items-center gap-1 text-[var(--accent-link,#2d5295)] dark:text-[var(--accent-link-dark,#93c5fd)]">
                          <Link2 size={13} /> Linked to academic record
                        </span>
                      ) : s.category === 'teaching' ? (
                        <span className="ml-auto flex items-center gap-1 text-gray-400">
                          <GraduationCap size={13} /> Teaching
                        </span>
                      ) : null}
                    </div>
                  </SoftCard>
                </button>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-sm text-gray-500 py-8">
              No staff match your search.
            </p>
          )}
        </>
      )}

      {adding && (
        <AddStaffModal
          onClose={() => setAdding(false)}
          staff={data.staff}
          allStaff={data.staff}
          teachers={teachers}
        />
      )}
      {importing && (
        <StaffMigrationImport
          staff={data.staff}
          profiles={data.payrollProfiles}
          allocations={data.leaveAllocations}
          onClose={() => setImporting(false)}
        />
      )}
      {importingDed && (
        <DeductionImport
          staff={data.staff}
          deductions={data.deductions}
          onClose={() => setImportingDed(false)}
        />
      )}
    </div>
  );
}

/* ==================================================================
   ADD STAFF MODAL
   ================================================================== */
function AddStaffModal({
  onClose,
  staff,
  allStaff,
  teachers,
}: {
  onClose: () => void;
  staff: { id: string; employeeNo?: string; category: StaffCategory; startDate?: string }[];
  allStaff: { linkedTeacherId?: string }[];
  teachers: { id: string; fullName: string; email?: string }[];
}) {
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [category, setCategory] = useState<StaffCategory>('teaching');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [residency, setResidency] = useState<ResidencyStatus>('citizen');
  const [employeeNo, setEmployeeNo] = useState(() => nextStaffNumber('teaching', staff));
  /* THE BRIDGE BETWEEN THE TWO HALVES OF THE SCHOOL. lib/hr calls
     linkedTeacherId "the ONLY bridge between the domains", the directory and
     the staff profile both display it — and this form, the one place a
     teacher is added by hand, had no way to set it. 58 of 62 teaching staff
     are linked because the migration import did it; every teacher added
     since has been an island. */
  const [linkedTeacherId, setLinkedTeacherId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // New staff auto-get the next number in their group; updates with category.
  useEffect(() => { setEmployeeNo(nextStaffNumber(category, staff)); }, [category, staff]);

  /* Teacher profiles not already claimed by another staff record — a
     teacher belongs to one person, and offering a taken one invites two
     staff records pointing at the same teacher. */
  const linkedElsewhere = useMemo(
    () => new Set(allStaff.map((x) => x.linkedTeacherId).filter(Boolean) as string[]),
    [allStaff],
  );
  const freeTeachers = useMemo(
    () => teachers
      .filter((t) => !linkedElsewhere.has(t.id))
      .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [teachers, linkedElsewhere],
  );

  /* Typing the name suggests the profile. The office creates the teacher
     profile first and the HR record second, so the match is almost always
     there — but it is a SUGGESTION: it fills the box, and whoever is at the
     desk can change it. Guessing silently is how the wrong teacher gets
     attached to somebody's salary. */
  const suggestion = useMemo(() => {
    const last = lastName.trim().toLowerCase();
    if (last.length < 3 || linkedTeacherId) return undefined;
    const hits = freeTeachers.filter((t) => t.fullName.toLowerCase().includes(last));
    return hits.length === 1 ? hits[0] : undefined;
  }, [lastName, freeTeachers, linkedTeacherId]);

  useEffect(() => {
    if (suggestion) {
      setLinkedTeacherId(suggestion.id);
      if (!email.trim() && suggestion.email) setEmail(suggestion.email);
    }
  }, [suggestion]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSave =
    firstName.trim() && lastName.trim() && jobTitle.trim() && employeeNo.trim();

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setErr(null);
    try {
      const id = newId(HR_COLLECTIONS.staff);
      await hr.staff.set(id, {
        employeeNo: employeeNo.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        category,
        jobTitle: jobTitle.trim(),
        department: department.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        residency,
        ...(linkedTeacherId ? { linkedTeacherId } : {}),
        status: 'active',
        startDate: new Date().toISOString(),
      });
      await recordHrAudit({
        action: 'staff.create',
        actor,
        subjectId: id,
        after: { employeeNo: employeeNo.trim(), name: `${firstName.trim()} ${lastName.trim()}`, category, jobTitle: jobTitle.trim(), linkedTeacherId: linkedTeacherId || null },
      });
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormModal
      onClose={onClose}
      icon={UserPlus}
      title="Add a staff member"
      maxWidth="max-w-lg"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <AccentButton
            icon={saving ? Loader2 : Save}
            disabled={!canSave || saving}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save staff'}
          </AccentButton>
        </>
      }
    >
      <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={glassFieldClass('py-2')}
              />
            </Field>
            <Field label="Last name">
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={glassFieldClass('py-2')}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee number">
              <input
                value={employeeNo}
                onChange={(e) => setEmployeeNo(e.target.value)}
                className={glassFieldClass('py-2')}
              />
            </Field>
            <Field label="Category">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as StaffCategory)}
                className={glassFieldClass('py-2')}
              >
                {(['teaching', 'management', 'admin', 'support', 'ancillary'] as StaffCategory[]).map(
                  (c) => (
                    <option key={c} value={c}>
                      {STAFF_CATEGORY_LABELS[c]}
                    </option>
                  ),
                )}
              </select>
            </Field>
          </div>
          <Field label="Job title">
            <input
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g. Mathematics Teacher"
              className={glassFieldClass('py-2')}
            />
          </Field>
          {category === 'teaching' && (
            <Field label="Academic teacher profile">
              <select
                value={linkedTeacherId}
                onChange={(e) => setLinkedTeacherId(e.target.value)}
                className={glassFieldClass('py-2')}
              >
                <option value="">Not linked</option>
                {freeTeachers.map((t) => (
                  <option key={t.id} value={t.id}>{t.fullName}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500 mt-1">
                {linkedTeacherId
                  ? 'Their marks, classes and this salary record are the same person.'
                  : 'Link the profile the office already created, so marks and salary belong to one person.'}
              </p>
            </Field>
          )}
          <Field label="Department (optional)">
            <input
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className={glassFieldClass('py-2')}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email (optional)">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={glassFieldClass('py-2')}
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={glassFieldClass('py-2')}
              />
            </Field>
          </div>
          <Field label="Residency">
            <select
              value={residency}
              onChange={(e) => setResidency(e.target.value as ResidencyStatus)}
              className={glassFieldClass('py-2')}
            >
              {(['citizen', 'work_permit', 'residence', 'pr'] as ResidencyStatus[]).map((r) => (
                <option key={r} value={r}>
                  {RESIDENCY_LABELS[r]}
                </option>
              ))}
            </select>
          </Field>

          {err && (
            <p className="text-sm text-red-600 dark:text-red-400">{err}</p>
          )}
      </div>
    </FormModal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
        {label}
      </span>
      {children}
    </label>
  );
}
